"use client";

import { cn } from "@garther/ui/libs";
import { AlertTriangle, Info, LucideIcon, Trash } from "lucide-react";

import { Button } from "../Button";
import { Popup } from "../Popup";

type ConfirmDialogVariant = "danger" | "warning" | "info";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  isLoading?: boolean;
  icon?: LucideIcon;
  className?: string;
}

const variantConfig: Record<
  ConfirmDialogVariant,
  {
    iconBg: string;
    iconColor: string;
    buttonBg: string;
    buttonHover: string;
    defaultIcon: LucideIcon;
  }
> = {
  danger: {
    iconBg: "bg-error-50",
    iconColor: "text-error-500",
    buttonBg: "bg-error-500",
    buttonHover: "hover:bg-error-600",
    defaultIcon: Trash
  },
  warning: {
    iconBg: "bg-warning-50",
    iconColor: "text-warning-500",
    buttonBg: "bg-warning-500",
    buttonHover: "hover:bg-warning-600",
    defaultIcon: AlertTriangle
  },
  info: {
    iconBg: "bg-info-50",
    iconColor: "text-info-500",
    buttonBg: "bg-info-500",
    buttonHover: "hover:bg-info-600",
    defaultIcon: Info
  }
};

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  isLoading = false,
  icon,
  className
}: ConfirmDialogProps) {
  const config = variantConfig[variant];
  const IconComponent = icon || config.defaultIcon;

  return (
    <Popup isOpen={isOpen} onClose={onClose} showCloseButton={false}>
      <div className={cn("max-w-md space-y-6", className)}>
        <div className="space-y-3">
          <div
            className={cn("flex items-center justify-center w-12 h-12 rounded-full mx-auto", config.iconBg)}
          >
            <IconComponent className={cn("h-6 w-6", config.iconColor)} />
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-responsive-lg font-semibold text-text-primary">{title}</h3>
            <p className="text-responsive-base font-normal text-text-secondary">{message}</p>
          </div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 border-border hover:bg-background-highlight"
          >
            {cancelText}
          </Button>
          <Button
            variant="default"
            onClick={onConfirm}
            disabled={isLoading}
            className={cn("flex-1 text-white", config.buttonBg, config.buttonHover)}
          >
            {isLoading ? "Loading..." : confirmText}
          </Button>
        </div>
      </div>
    </Popup>
  );
}
